// Telecalling/frontend/src/services/api.js
import axios from 'axios';
import { tokenStorage } from '../utils/tokenStorage'; // 🔥 NEW: Cross-subdomain auth support

// Debug environment variables
// Use ONLY the environment variable - no fallback
const getBaseURL = () => {
  const envURL = process.env.REACT_APP_API_URL;

  if (!envURL) {
    throw new Error('REACT_APP_API_URL environment variable is required');
  }
  return envURL;
};

// Create axios instance with base configuration
const api = axios.create({
  baseURL: getBaseURL(),
  // Increase default to be tolerant of network latency but keep requests short
  // OTP calls use per-call timeouts as well.
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true', // Bypass ngrok browser warning page
  },
});

// Helper functions for token management - NOW USING COOKIES FOR CROSS-SUBDOMAIN SUPPORT
const getAccessToken = () => tokenStorage.getApiAccessToken();
const getRefreshToken = () => tokenStorage.getRefreshToken();
const setTokens = (accessToken, refreshToken) => {
  tokenStorage.setTokens(accessToken, refreshToken);
};
const clearTokens = () => {
  tokenStorage.clearAuth();
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // ✅ FIX: Don't overwrite the Authorization header if it's already provided
    // This allows RBAC routes to send their specific tokens
    const token = getAccessToken();
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If the data is FormData, remove Content-Type header to let browser set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh queue to prevent concurrent refresh attempts
let refreshPromise = null;
let isRefreshing = false;

// Response interceptor to handle token refresh with queue support
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // ✅ FIX: Handle 401 errors with queue to prevent race conditions
    if (error.response?.status === 401 && !originalRequest._retry) {
      // ⚠️ ISOLATION FIX: If this is an RBAC specific route, don't trigger global logout/refresh.
      // The RBAC component (AdminUsersDashboardLayout) will handle its own 401.
      if (originalRequest.url?.includes('/admin-rbac/')) {
        return Promise.reject(error);
      }

      // Don't attempt refresh or clear tokens if we're not even logged in
      if (!getAccessToken() && !getRefreshToken()) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If refresh is already in progress, wait for it
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          // Retry original request with new token
          const token = getAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (waitError) {
          // Refresh failed, continue to error handling
        }
      }

      // Start new refresh attempt
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = (async () => {
          try {
            const refreshToken = getRefreshToken();
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }

            const refreshResponse = await axios.post(`${api.defaults.baseURL}/auth/refresh-token`, {
              refreshToken: refreshToken
            });

            const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data.tokens;

            setTokens(accessToken, newRefreshToken);

            // Update all pending requests with new token
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;

            return { accessToken, newRefreshToken };
          } catch (refreshError) {
            clearTokens();
            // Only redirect if not already on homepage
            if (window.location.pathname !== '/') {
              window.location.href = '/';
            }
            throw refreshError;
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        })();

        try {
          await refreshPromise;
          // Retry original request with new token
          const token = getAccessToken();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  // Sign up new user
  signup: (userData) => api.post('/auth/signup', userData),

  // Login user
  login: (credentials) => api.post('/auth/login', credentials),

  // Verify email with OTP
  verifyEmail: (data) => api.post('/auth/verify-email', data),

  // Resend verification OTP for email/phone identifiers
  resendVerification: (identifierOrPayload) => {
    if (typeof identifierOrPayload === 'string') {
      return api.post('/auth/resend-verification', { email: identifierOrPayload });
    }

    return api.post('/auth/resend-verification', identifierOrPayload || {});
  },

  // Request password reset (supports both email and phone identifiers)
  forgotPassword: (payload) => {
    if (typeof payload === 'string') {
      // If just a string, treat as email
      return api.post('/auth/forgot-password', { email: payload, authType: 'email' });
    }
    // For phone: {phone, method, authType} where method is 'sms' or 'whatsapp' (required for Indian numbers)
    // For email: just pass email string
    return api.post('/auth/forgot-password', payload);
  },

  // Reset password with OTP (supports both email and phone identifiers)
  resetPassword: (data) => {
    // data can be {email, otp, newPassword, authType} for email or {phone, otp, newPassword, authType} for phone
    return api.post('/auth/reset-password', data);
  },

  // Refresh access token
  refreshToken: (refreshToken) => api.post('/auth/refresh-token', { refreshToken }),

  // Logout user
  logout: (refreshToken) => api.post('/auth/logout', { refreshToken }),

  // Get current user profile
  getProfile: () => api.get('/auth/me'),

  // Update user profile
  updateProfile: (data) => api.put('/auth/profile', data),

  // Google OAuth
  googleAuth: (data) => api.post('/auth/google', data),

  // Legacy methods for backward compatibility
  register: (userData) => api.post('/auth/signup', userData),
  changePassword: (data) => api.put('/auth/change-password', data),
};

// Client API
export const clientAPI = {
  create: (data) => api.post('/clients', data),
  getProfile: () => api.get('/clients/profile'),
  updateProfile: (data) => api.put('/clients/profile', data),
  updateMaxDeliveryDays: (max_delivery_days) => api.put('/clients/max-delivery-days', { max_delivery_days }),
  updateEditLockTime: (edit_lock_time) => api.put('/clients/edit-lock-time', { edit_lock_time }),
  getDashboardOverview: () => api.get('/clients/dashboard/overview'),
};

// Customization API
export const customizationAPI = {
  getSettings: () => api.get('/customization'),
  updateSettings: (data) => api.put('/customization', data),
  enhanceText: (text, type) => api.post('/customization/enhance', { text, type }),
};

// Products API
export const productsAPI = {
  getAll: (params = {}) => api.get('/products', { params }),
  getFilters: () => api.get('/products/filters'),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  bulkImport: (data) => api.post('/products/bulk-import', data),
  getCategories: () => api.get('/products/categories'),
  // Excel template upload
  downloadTemplate: () => {
    const token = getAccessToken();
    return fetch(`${api.defaults.baseURL}/products/excel-template/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true'
      }
    }).then(res => {
      if (!res.ok) throw new Error('Failed to download template');
      return res.blob();
    });
  },
  excelUpload: (formData) => api.post('/products/excel-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes for large files
  }),
  getExcelUploadProgress: (uploadId) => api.get(`/products/excel-upload/progress/${uploadId}`),
  // Image management
  uploadImages: (productId, formData) => api.post(`/products/${productId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getImages: (productId) => api.get(`/products/${productId}/images`),
  deleteImage: (productId, imageId) => api.delete(`/products/${productId}/images/${imageId}`),
  setPrimaryImage: (productId, imageId) => api.put(`/products/${productId}/images/${imageId}/primary`)
};

// Services API
export const servicesAPI = {
  getAll: (params = {}) => api.get('/services', { params }),
  getFilters: () => api.get('/services/filters'),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  bulkDelete: (serviceIds) => api.post('/services/bulk-delete', { serviceIds }),
  aiUpload: (formData) => api.post('/services/ai-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 30000, // 30 seconds for initial response (backend returns uploadId immediately)
  }),
  // Excel template upload
  downloadTemplate: () => {
    const token = getAccessToken();
    return fetch(`${api.defaults.baseURL}/services/excel-template/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true'
      }
    }).then(res => {
      if (!res.ok) throw new Error('Failed to download template');
      return res.blob();
    });
  },
  excelUpload: (formData) => api.post('/services/excel-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes for large files
  }),
  getExcelUploadProgress: (uploadId) => api.get(`/services/excel-upload/progress/${uploadId}`),
  enhanceText: (text, type) => api.post('/services/enhance-text', { text, type }),
  getCategories: () => api.get('/services/categories'),
  // Image management
  uploadImages: (serviceId, formData) => api.post(`/services/${serviceId}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getImages: (serviceId) => api.get(`/services/${serviceId}/images`),
  deleteImage: (serviceId, imageId) => api.delete(`/services/${serviceId}/images/${imageId}`),
  setPrimaryImage: (serviceId, imageId) => api.put(`/services/${serviceId}/images/${imageId}/primary`),

  // STID Management
  fetchByStid: (stid) => api.post('/services/fetch-by-stid', { stid }),
  exportStidExcel: () => {
    const token = getAccessToken();
    return fetch(`${api.defaults.baseURL}/services/export-stid-excel`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'ngrok-skip-browser-warning': 'true'
      }
    }).then(res => {
      if (!res.ok) throw new Error('Failed to export STID Excel');
      return res.blob();
    });
  },
  bulkFetchByStid: (formData) => api.post('/services/bulk-fetch-by-stid', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
};

// Appointments API
export const appointmentsAPI = {
  getAll: (params) => api.get('/appointments', { params }),
  createSlot: (data) => api.post('/appointments/slots', data),
  book: (slotId, data) => api.post(`/appointments/${slotId}/book`, data),
  cancel: (slotId) => api.put(`/appointments/${slotId}/cancel`),
  delete: (slotId) => api.delete(`/appointments/${slotId}`),
  getAvailable: (clientId, params) => api.get(`/appointments/available/${clientId}`, { params }),
  bulkCreate: (data) => api.post('/appointments/bulk-create', data),
};

// Call Logs API
const savePbxCdrToEndpoint = async (records) => {
  const endpoints = [
    '/call-logs/pbx-sync',
    '/admin/call-logs/pbx-sync',
    '/call-logs/sync',
    '/admin/call-logs/sync',
    '/call-logs/bulk-sync',
    '/admin/call-logs/bulk-sync'
  ];

  let lastError = null;
  for (const endpoint of endpoints) {
    try {
      return await api.post(endpoint, { records });
    } catch (error) {
      lastError = error;
      if (error.response?.status !== 404) {
        throw error;
      }
      console.warn(`savePbxCdr: endpoint ${endpoint} returned 404, trying next fallback.`);
    }
  }

  const attempted = endpoints.join(', ');
  const message = `PBX CDR sync route not found. Tried: ${attempted}`;
  console.error(message, lastError?.response?.data || lastError?.message || lastError);
  const error = new Error(message);
  error.original = lastError;
  throw error;
};

export const callLogsAPI = {
  getAll: (params) => api.get('/call-logs', { params }),
  getById: (id) => api.get(`/call-logs/${id}`),
  update: (id, data) => api.put(`/call-logs/${id}`, data),
  delete: (id) => api.delete(`/call-logs/${id}`),
  getStats: (params) => api.get('/call-logs/stats/overview', { params }),
  // Save PBX CDR records into call_logs table (bulk upsert by call_uuid)
  savePbxCdr: (records) => savePbxCdrToEndpoint(records),
};

// WhatsApp API
export const whatsappAPI = {
  send: (data) => api.post('/whatsapp/send', data),
  getLogs: (params) => api.get('/whatsapp/logs', { params }),
  sendEstimate: (data) => api.post('/whatsapp/send-estimate', data),
  sendPaymentLink: (data) => api.post('/whatsapp/send-payment-link', data),
};

// Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  send: (id, data) => api.post(`/invoices/${id}/send`, data),
  sendEmail: (id, emailData) => api.post(`/invoices/${id}/send-email`, emailData),
  markPaid: (id, data) => api.post(`/invoices/${id}/mark-paid`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  getStats: (params) => api.get('/invoices/stats/overview', { params }),
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Project Templates API
export const projectTemplatesAPI = {
  getAll: () => api.get('/project-templates'),
  getById: (id) => api.get(`/project-templates/${id}`),
  create: (data) => api.post('/project-templates', data),
  update: (id, data) => api.put(`/project-templates/${id}`, data),
  delete: (id) => api.delete(`/project-templates/${id}`)
};

// Payments API
export const paymentsAPI = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  createLink: (data) => api.post('/payments/create-link', data),
  updateStatus: (id, data) => api.put(`/payments/${id}/status`, data),
  resend: (id, data) => api.post(`/payments/${id}/resend`, data),
  cancel: (id) => api.post(`/payments/${id}/cancel`),
  getStats: (params) => api.get('/payments/stats/overview', { params }),
};

// Support API
export const supportAPI = {
  getTickets: (params) => api.get('/support', { params }),
  getTicket: (id) => api.get(`/support/${id}`),
  createTicket: (data) => api.post('/support', data),
  updateTicket: (id, data) => api.put(`/support/${id}`, data),
  closeTicket: (id, data) => api.post(`/support/${id}/close`, data),
  reopenTicket: (id, data) => api.post(`/support/${id}/reopen`, data),
  addComment: (id, data) => api.post(`/support/${id}/comments`, data),
  getFAQ: (params) => api.get('/support/faq/items', { params }),
};

// Dashboard API
export const dashboardAPI = {
  // Get comprehensive dashboard stats
  getStats: (params = {}) => api.get('/dashboard/stats', { params }),

  // Get chart data for various time periods
  getChartData: (params = {}) => api.get('/dashboard/charts', { params }),

  // Get recent activity
  getRecentActivity: (params = {}) => api.get('/dashboard/recent', { params }),

  // Get overview data
  getOverview: (params = {}) => api.get('/dashboard/overview', { params })
};

// Admin Clients API
export const adminClientsAPI = {
  createLinked: (data) => api.post('/admin-users/clients/create-linked', data),
  getClientsAdmin: () => api.get('/admin-users/clients'),
  getClientsUser: () => api.get('/admin/clients')
};

// Billing API
export const billingAPI = {
  getSubscription: () => api.get('/billing/subscription'),
  notifyHqRenewal: () => api.post('/billing/notify-hq-renewal'),
  purchaseTopup: (data) => api.post('/billing/topup', data)
};

// Admin API
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getClients: (params) => api.get('/admin/clients', { params }),
  getClient: (id) => api.get(`/admin/clients/${id}`),
  updateClientStatus: (id, data) => api.put(`/admin/clients/${id}/status`, data),
  assignNumber: (id, data) => api.post(`/admin/clients/${id}/assign-number`, data),
  getCallLogs: (params) => api.get('/admin/call-logs', { params }),
  getSupportTickets: (params) => api.get('/admin/support-tickets', { params }),
  updateSupportTicket: (id, data) => api.put(`/admin/support-tickets/${id}`, data),
  getStats: (params) => api.get('/admin/stats', { params }),
  getActions: (params) => api.get('/admin/actions', { params }),
};

// Requirements API
export const requirementsAPI = {
  getRequirements: () => api.get('/requirements'),
  updateRequirements: (data) => api.put('/requirements', data),
  createRequirements: (data) => api.post('/requirements', data),
  deleteRequirements: () => api.delete('/requirements'),
  fetchRoles: () => api.get('/rbac/roles'), // For RBAC validation
};

// Bot Features API
export const botFeaturesAPI = {
  get: () => api.get('/bot-features'),
  update: (data) => api.put('/bot-features', data),
};

// Utility functions
export const uploadFile = async (file, endpoint) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const downloadFile = async (url, filename) => {
  const response = await api.get(url, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

// Authentication helper functions - NOW USING COOKIES FOR CROSS-SUBDOMAIN SUPPORT
export const authHelpers = {
  // Check if user is logged in
  isAuthenticated: () => {
    return tokenStorage.isAuthenticated();
  },

  // Get current user from cookie storage
  getCurrentUser: () => {
    return tokenStorage.getCurrentUser();
  },

  // Set current user in cookie storage
  setCurrentUser: (user) => {
    tokenStorage.setCurrentUser(user);
  },

  // Clear all auth data
  clearAuth: () => {
    tokenStorage.clearAuth();
  },

  // Set tokens and user data
  setAuthData: (tokens, user) => {
    // Critical security: Clear any impersonation tokens before setting new auth data
    // This ensures franchises logging in normally don't inherit impersonation state
    tokenStorage.clearAuth(); // Clear everything first

    setTokens(tokens.accessToken, tokens.refreshToken);
    tokenStorage.setCurrentUser(user);
  },

  // Check if user email is verified
  isEmailVerified: () => {
    const user = tokenStorage.getCurrentUser();
    return user?.isEmailVerified || false;
  },

  // Get user's business name
  getBusinessName: () => {
    const user = tokenStorage.getCurrentUser();
    return user?.businessName || null;
  },

  // Get user's username
  getUsername: () => {
    const user = tokenStorage.getCurrentUser();
    return user?.username || null;
  }
};

// Availability API
export const availabilityAPI = {
  // Get client availability schedule
  getSchedule: () => api.get('/availability'),

  // Update client availability schedule
  updateSchedule: (data) => api.put('/availability', data),

  // Set availability for a specific day
  setDayAvailability: (dayOfWeek, data) => api.put(`/availability/day/${dayOfWeek}`, data),

  // Disable availability for a specific day
  disableDayAvailability: (dayOfWeek) => api.delete(`/availability/day/${dayOfWeek}`),

  // Auto-confirmation settings
  getAutoConfirmSettings: () => api.get('/availability/auto-confirm'),
  updateAutoConfirmSettings: (data) => api.put('/availability/auto-confirm', data),

  // Default employee settings
  getDefaultEmployee: () => api.get('/availability/default-employee'),
  updateDefaultEmployee: (data) => api.put('/availability/default-employee', data)
};

// Credits API
export const creditsAPI = {
  // Get current credit balance
  getBalance: () => api.get('/credits/balance'),

  // Get credit usage history
  getUsageHistory: (params = {}) => api.get('/credits/usage-history', { params }),

  // Get credit usage breakdown
  getUsageBreakdown: (params = {}) => api.get('/credits/usage-breakdown', { params }),

  // Add credits to account
  addCredits: (data) => api.post('/credits/add', data),

  // Deduct credits from account
  deductCredits: (data) => api.post('/credits/deduct', data),

  // Get credit operation rates
  getRates: () => api.get('/credits/rates')
};

// Appointment Customer Link API
export const appointmentCustomerAPI = {
  // Batch check if phones exist in customers table and if linked to current client
  checkLinks: (phones) => api.post('/customers/check-appointment-links', { phones }),

  // Link an existing customer to the current client
  linkToClient: (customerId) => api.post('/customers/link-to-client', { customerId }),

  // Create a new customer and link them to the current client
  createAndLink: (data) => api.post('/customers/create-and-link', data)
};

// Appointments API (New)
export const appointmentsV2API = {
  // Get all appointments with filtering
  getAppointments: (params = {}) => api.get('/appointments-v2', { params }),

  // Get appointment statistics
  getStats: (params = {}) => api.get('/appointments-v2/stats', { params }),

  // Get specific appointment
  getAppointment: (appointmentId) => api.get(`/appointments-v2/${appointmentId}`),

  // Create new appointment (manual booking)
  createAppointment: (data) => api.post('/appointments-v2', data),

  // Update appointment
  updateAppointment: (appointmentId, data) => api.put(`/appointments-v2/${appointmentId}`, data),

  // Delete/Cancel appointment
  deleteAppointment: (appointmentId, permanent = false) =>
    api.delete(`/appointments-v2/${appointmentId}`, { params: { permanent } }),

  // Book appointment (public endpoint for bot)
  bookAppointment: (clientId, data) => api.post(`/appointments-v2/book/${clientId}`, data),

  // Bulk cancel appointments
  bulkCancelAppointments: (data) => api.post('/appointments-v2/bulk-cancel', data),

  // Get available time slots
  getAvailableSlots: (clientId, date, interval) =>
    api.get(`/appointments-v2/available-slots/${clientId}/${date}`, { params: { interval } }),

  // Get billing info for an appointment
  getBillingInfo: (appointmentId) => api.get(`/appointments-v2/billing-info/${appointmentId}`)
};

// Inventory API
export const inventoryAPI = {
  // Overview and Analytics
  getOverview: () => api.get('/inventory/overview'),
  getAnalytics: (params) => api.get('/inventory/analytics', { params }),

  // Product Inventory
  getProductInventory: (params) => api.get('/inventory/products', { params }),
  getProductInventoryById: (productId) => api.get(`/inventory/products/${productId}`),
  updateProductInventory: (productId, data) => api.put(`/inventory/products/${productId}`, data),
  initializeProductInventory: (productId, data) => api.post(`/inventory/products/${productId}/initialize`, data),

  // Stock Movements
  getStockMovements: (params) => api.get('/inventory/movements', { params }),
  createStockMovement: (data) => api.post('/inventory/movements', data),

  // Stock Adjustments
  getStockAdjustments: (params) => api.get('/inventory/adjustments', { params }),
  createStockAdjustment: (data) => api.post('/inventory/adjustments', data),

  // Suppliers
  getSuppliers: (params) => api.get('/inventory/suppliers', { params }),
  getSupplierById: (id) => api.get(`/inventory/suppliers/${id}`),
  createSupplier: (data) => api.post('/inventory/suppliers', data),
  updateSupplier: (id, data) => api.put(`/inventory/suppliers/${id}`, data),
  deleteSupplier: (id) => api.delete(`/inventory/suppliers/${id}`),

  // Low Stock Alerts
  getLowStockAlerts: (params) => api.get('/inventory/alerts', { params }),
  resolveAlert: (id) => api.put(`/inventory/alerts/${id}/resolve`),
  bulkResolveAlerts: (alertIds) => api.put('/inventory/alerts/bulk-resolve', { alertIds }),

  // Reports
  getValuationReport: () => api.get('/inventory/reports/valuation'),
  getMovementReport: (params) => api.get('/inventory/reports/movements', { params }),
};

// Orders API
export const ordersAPI = {
  // Get all orders with pagination and filtering
  getAll: (params = {}) => api.get('/orders', { params }),

  // Get single order by ID
  getById: (id) => api.get(`/orders/${id}`),

  // Create new order
  create: (data) => api.post('/orders', data),

  // Update order
  update: (id, data) => api.put(`/orders/${id}`, data),

  // Delete order
  delete: (id) => api.delete(`/orders/${id}`),

  // Cancel order and revert inventory
  cancel: (id, reason) => api.post(`/orders/${id}/cancel`, { reason }),

  // Update order status
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),

  // Get order statistics
  getStats: (params) => api.get('/orders/stats/overview', { params }),

  // Export orders
  export: (params) => api.get('/orders/export', { params, responseType: 'blob' }),

  // Orders to Tasks Conversion
  convertToTask: (orderId, data) => api.post(`/orders/${orderId}/convert-to-task`, data),
  bulkConvertToTasks: (data) => api.post('/orders/bulk-convert-to-tasks', data),
  autoConvertUrgent: () => api.post('/orders/auto-convert-urgent'),
  getConversionStats: () => api.get('/orders/conversion-stats'),

  // Bulk operations
  bulkUpdate: (updates) => api.post('/orders/bulk-update', { updates }),
  bulkDelete: (ids) => api.post('/orders/bulk-delete', { ids }),

  // Dispatch notes operations
  fetchDispatchNotes: (id) => api.get(`/orders/${id}/dispatch-notes`),
  createDispatchNotes: (id, data) => api.post(`/orders/${id}/dispatch-notes`, data),

  // Dispatch documents operations
  fetchDispatchDocuments: (dispatchNumber) => api.get(`/orders/dispatch-documents/${dispatchNumber}`),
  deleteDispatchDocument: (documentId) => api.delete(`/orders/dispatch-documents/${documentId}`),
  uploadDispatchDocuments: (dispatchNumber, formData) => api.post(`/orders/dispatch-documents/${dispatchNumber}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Daily Orders API
export const dailyOrdersAPI = {
  // Get all daily orders with pagination and filtering
  getAll: (params = {}) => api.get('/daily-orders', { params }),

  // Get single daily order by ID
  getById: (id) => api.get(`/daily-orders/${id}`),

  // Get all items for a daily order
  getItems: (id) => api.get(`/daily-orders/${id}/items`),

  // Create new daily order
  create: (data) => api.post('/daily-orders', data),

  // Update daily order
  update: (id, data) => api.put(`/daily-orders/${id}`, data),

  // Delete/Cancel daily order
  delete: (id, data = {}) => api.delete(`/daily-orders/${id}`, { data }),

  // Pause daily order
  pause: (id, data) => api.post(`/daily-orders/${id}/pause`, data),

  // Resume daily order
  resume: (id) => api.post(`/daily-orders/${id}/resume`),

  // Get daily order instances
  getInstances: (id, params = {}) => api.get(`/daily-orders/${id}/instances`, { params }),

  // Get statistics
  getStats: (params) => api.get('/daily-orders/stats', { params }),

  // Assign employee to daily order
  assignEmployee: (id, data) => api.post(`/daily-orders/${id}/assign-employee`, data),

  // Update instance status (Manual delivery marking)
  updateInstanceStatus: (instanceId, data) => api.put(`/daily-orders/instances/${instanceId}/status`, data),

  // Edit instances (for specific day or future days)
  editInstances: (id, data) => api.post(`/daily-orders/${id}/edit-instances`, data),

  // Subscription Dispatch Notes
  getSubscriptionDispatchPreview: (date, warehouseId) => {
    const params = warehouseId ? { warehouseId } : {};
    return api.get(`/daily-orders/subscription-dispatch-preview/${date}`, { params, timeout: 30000 });
  },
  createSubscriptionDispatchNote: (data) => {
    // If data contains files, use FormData
    if (data.documents && data.documents.length > 0) {
      const formData = new FormData();
      formData.append('deliveryDate', data.deliveryDate);
      formData.append('items', JSON.stringify(data.items));
      formData.append('notes', data.notes || '');
      formData.append('assignedEmployee', data.assignedEmployee);
      formData.append('customerBreakdown', JSON.stringify(data.customerBreakdown || []));
      if (data.warehouseId) formData.append('warehouseId', data.warehouseId);
      data.documents.forEach(doc => formData.append('documents', doc));
      return api.post('/daily-orders/subscription-dispatch-notes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
    }
    return api.post('/daily-orders/subscription-dispatch-notes', data, { timeout: 60000 });
  },
  getSubscriptionDispatchNotes: (params = {}) => api.get('/daily-orders/subscription-dispatch-notes', { params }),
  getSubscriptionDispatchDetails: (date) => api.get(`/daily-orders/subscription-dispatch-notes/${date}/details`),
  fetchDispatchNotes: (id) => api.get(`/daily-orders/${id}/dispatch-notes`)
};

// Admin Daily Orders API (for Admin Users)
export const adminDailyOrdersAPI = {
  // Get all subscription orders assigned to this admin
  getAll: (params = {}) => api.get('/admin-users/subscription-orders', { params }),

  // Get detailed subscription order info
  getById: (id) => api.get(`/admin-users/subscription-orders/${id}`),

  // Get dispatch notes for a specific subscription order
  getDispatchNotes: (orderId) => api.get(`/admin-users/subscription-orders/${orderId}/dispatch-notes`),

  // Get dispatch preview for a specific date across all assigned clients
  getSubscriptionDispatchPreview: (date) => api.get(`/admin-users/subscription-dispatch-preview/${date}`, { timeout: 30000 }),

  // Create subscription dispatch note (admin version)
  createSubscriptionDispatchNote: (data) => {
    // If data contains files, use FormData
    if (data.documents && data.documents.length > 0) {
      const formData = new FormData();
      formData.append('deliveryDate', data.deliveryDate);
      formData.append('items', typeof data.items === 'string' ? data.items : JSON.stringify(data.items));
      formData.append('notes', data.notes || '');
      formData.append('assignedEmployee', data.assignedEmployee);
      formData.append('customerBreakdown', typeof data.customerBreakdown === 'string' ? data.customerBreakdown : JSON.stringify(data.customerBreakdown || []));
      formData.append('dispatchSource', data.dispatchSource);
      if (data.warehouseId) formData.append('warehouseId', data.warehouseId);
      data.documents.forEach(doc => formData.append('documents', doc));
      return api.post('/admin-users/subscription-dispatch-notes', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
    }
    return api.post('/admin-users/subscription-dispatch-notes', data, { timeout: 60000 });
  },

  // Get all subscription dispatch notes for this admin (Historical)
  getSubscriptionDispatchNotes: (params = {}) => api.get('/admin-users/subscription-dispatch-notes', { params }),

  // Update status of a subscription dispatch item (Admin)
  updateDispatchItemStatus: (itemId, data) => api.patch(`/admin-users/subscription-dispatch-notes/items/${itemId}/status`, data),
};
export const billsAPI = {
  // Get all bills with filtering and pagination
  getAll: (params = {}) => api.get('/bills', { params }),

  // Get single bill by ID
  getById: (billId) => api.get(`/bills/${billId}`),

  // Create new bill
  create: (data) => api.post('/bills', data),

  // Generate bill from order
  generateFromOrder: (orderId) => api.post(`/bills/from-order/${orderId}`),

  // Update bill
  update: (billId, data) => api.put(`/bills/${billId}`, data),

  // Delete bill
  delete: (billId) => api.delete(`/bills/${billId}`),

  // Update bill status
  updateStatus: (billId, status) => api.put(`/bills/${billId}/status`, { status }),

  // Mark bill as paid
  markPaid: (billId, data) => api.post(`/bills/${billId}/mark-paid`, data),

  // Get bill statistics
  getStats: (params) => api.get('/bills/stats', { params }),

  // Export bills
  export: (params) => api.get('/bills/export', { params, responseType: 'blob' }),

  // Bulk operations
  bulkUpdate: (updates) => api.post('/bills/bulk-update', { updates }),
  bulkDelete: (ids) => api.post('/bills/bulk-delete', { ids }),

  // Send bill via email
  sendEmail: (billId, emailData) => api.post(`/bills/${billId}/send-email`, emailData),

  // Download PDF
  downloadPDF: (billId) => api.get(`/bills/${billId}/pdf`, { responseType: 'blob' }),

  // Send bill (legacy)
  send: (billId, data) => api.post(`/bills/${billId}/send`, data)
};

// Finance Ledger API
export const financeAPI = {
  // Get ledger history with pagination and running balances
  getLedger: (params = {}) => api.get('/finance/ledger', { params }),
  // Get KPI summary (total revenue, total expense, net balance)
  getSummary: (params = {}) => api.get('/finance/ledger/summary', { params }),
  // Get statistical aggregates for charts
  getStats: (params = {}) => api.get('/finance/ledger/stats', { params }),
  // Create a manual ledger entry
  createEntry: (data) => api.post('/finance/ledger', data),
  // Upload a receipt document
  uploadReceipt: (formData) => api.post('/finance/ledger/upload-receipt', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Bulk import financial statements
  bulkImport: (formData) => api.post('/finance/ledger/bulk-import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  // Delete a specific entry
  deleteEntry: (id) => api.delete(`/finance/ledger/${id}`),
  // Update a specific entry (e.g., set document_url)
  updateEntry: (id, data) => api.patch(`/finance/ledger/${id}`, data),
};

// Admin Users Finance Ledger API
export const adminUsersFinanceAPI = {
  getLedger: (params = {}) => api.get('/admin-users/finance/ledger', { params }),
  getSummary: (params = {}) => api.get('/admin-users/finance/ledger/summary', { params }),
  getStats: (params = {}) => api.get('/admin-users/finance/ledger/stats', { params }),
};

// Quick Bills API
export const quickBillsAPI = {
  // Get all quick bills with pagination and filtering
  getAll: (params = {}) => api.get('/quick-bills', { params }),

  // Get single quick bill by ID
  getById: (id) => api.get(`/quick-bills/${id}`),

  // Create new quick bill
  create: (data) => api.post('/quick-bills', data),

  // Update quick bill
  update: (id, data) => api.put(`/quick-bills/${id}`, data),

  // Delete quick bill
  delete: (id) => api.delete(`/quick-bills/${id}`),

  // Get quick bill statistics
  getStats: () => api.get('/quick-bills/analytics/stats'),

  // Download PDF
  downloadPDF: (id) => api.get(`/quick-bills/${id}/pdf`, { responseType: 'blob' })
};

// Quotations API
export const quotationsAPI = {
  // Get all quotations with pagination and filtering
  getAll: (params = {}) => api.get('/quotations', { params }),

  // Get single quotation by ID
  getById: (id) => api.get(`/quotations/${id}`),

  // Create new quotation
  create: (data) => api.post('/quotations', data),

  // Update quotation
  update: (id, data) => api.put(`/quotations/${id}`, data),

  // Delete quotation
  delete: (id) => api.delete(`/quotations/${id}`),

  // Update quotation status
  updateStatus: (id, status, data = {}) => api.patch(`/quotations/${id}/status`, { status, ...data }),

  // Convert quotation to order
  convertToOrder: (id) => api.post(`/quotations/${id}/convert-to-order`),

  // Get quotation statistics
  getStats: (params) => api.get('/quotations/stats/overview', { params }),

  // Export quotations
  export: (params) => api.get('/quotations/export', { params, responseType: 'blob' }),

  // Download PDF
  downloadPDF: (id) => api.get(`/quotations/${id}/pdf`, { responseType: 'blob' }),

  // Send quotation via email
  sendEmail: (id, emailData) => api.post(`/quotations/${id}/send-email`, emailData)
};

// Customer Management API
export const customersAPI = {
  // Get all customers for client with pagination and filtering
  getAll: (params = {}) => api.get('/customers/manage', { params, timeout: 30000 }),

  // Get single customer by ID
  getById: (customerId) => api.get(`/customers/manage/${customerId}`),

  // Create new customer
  create: (data) => api.post('/customers/manage', data),

  // Update customer
  update: (customerId, data) => api.put(`/customers/manage/${customerId}`, data),

  // Delete customer relationship (not customer record)
  delete: (customerId) => api.delete(`/customers/manage/${customerId}`),

  // Permanently delete customer record
  deletePermanent: (customerId) => api.delete(`/customers/permanent/${customerId}`),

  // Get customer statistics (existing endpoint)
  getStats: () => api.get('/customers/stats'),

  // ✅ NEW: Set address as primary
  setAddressAsPrimary: (customerId, addressId) =>
    api.post(`/customers/manage/${customerId}/set-primary-address/${addressId}`),

  // Legacy endpoints (for backward compatibility)
  legacy: {
    getAll: () => api.get('/customers'),
    getStats: () => api.get('/customers/stats')
  }
};

// Connection Code API
export const connectionCodeAPI = {
  // Generate a new connection code
  generate: () => api.post('/clients/connection-code/generate'),

  // Update existing connection code
  update: (connectionCode) => api.put('/clients/connection-code/update', { connectionCode }),

  // Get current connection code
  get: () => api.get('/clients/connection-code')
};

// Google Calendar Sync API
export const googleCalendarSyncAPI = {
  // Create or update Google Calendar sync configuration
  sync: (data) => api.post('/google-calendar-sync', data),

  // Get Google Calendar sync configuration
  get: () => api.get('/google-calendar-sync'),

  // Delete Google Calendar sync configuration
  delete: () => api.delete('/google-calendar-sync')
};


// Vendor Purchase Orders API
export const vendorPurchaseOrdersAPI = {
  getPurchaseOrders: (params) => api.get('/vendor-purchase-orders', { params }),
  getPurchaseOrderById: (id) => api.get(`/vendor-purchase-orders/${id}`),
  createPurchaseOrder: (data) => api.post('/vendor-purchase-orders', data),
  updatePurchaseOrder: (id, data) => api.put(`/vendor-purchase-orders/${id}`, data),
  deletePurchaseOrder: (id) => api.delete(`/vendor-purchase-orders/${id}`),
  getStats: () => api.get('/vendor-purchase-orders/stats'),
  receivePurchaseOrder: (id, data) => api.post(`/vendor-purchase-orders/${id}/receive`, data),
  getSubProducts: (poId) => api.get(`/vendor-sub-products/purchase-order/${poId}`),
  generatePDF: (id) => api.get(`/vendor-purchase-orders/${id}/generate-pdf`, { responseType: 'blob' }),
  sendEmail: (id, emailData) => api.post(`/vendor-purchase-orders/${id}/send-email`, emailData),
};

// Vendors API
export const vendorsAPI = {
  getAll: (params) => api.get('/vendors', { params }),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
  getVendorProducts: (vendorId) => api.get(`/vendors/${vendorId}/vendor-products`),
  searchProducts: (search) => api.get('/vendors/products/search', { params: { search } }),
};

// Warehouses API
export const warehousesAPI = {
  getAll: (params) => api.get('/warehouses', { params }),
  getById: (id) => api.get(`/warehouses/${id}`),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
  getProducts: (id) => api.get(`/warehouses/${id}/products`),
  getStock: (id) => api.get(`/warehouses/${id}/stock`),
};

// Dispatches API
export const dispatchesAPI = {
  // Get all dispatches with filtering
  getAll: (params = {}) => api.get('/dispatches', { params }),

  // Get single dispatch
  getById: (id) => api.get(`/dispatches/${id}`),

  // Create dispatch
  create: (data) => api.post('/dispatches', data),

  // Update dispatch
  update: (id, data) => api.put(`/dispatches/${id}`, data),

  // Delete dispatch
  delete: (id) => api.delete(`/dispatches/${id}`),

  // Get summary statistics
  getSummary: (params = {}) => api.get('/dispatches/summary', { params }),

  // Get employees for dropdown
  getEmployees: () => api.get('/dispatches/employees'),

  // Get dispatch documents
  getDocuments: (dispatchNumber) => api.get(`/dispatches/documents/${dispatchNumber}`),

  // Upload document
  uploadDocument: (dispatchNumber, formData) =>
    api.post(`/dispatches/documents/${dispatchNumber}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

  // Delete document
  deleteDocument: (documentId) => api.delete(`/dispatches/documents/${documentId}`),

  // Get orders grouped with dispatches
  getOrdersGrouped: (params = {}) => api.get('/dispatches/orders/grouped', { params }),

  // Get all dispatches for a specific order
  getOrderDispatches: (orderId, employeeId = null) => {
    const params = employeeId ? { employee_id: employeeId } : {};
    return api.get(`/dispatches/orders/${orderId}/dispatches`, { params });
  }
};

// Admin Requirements API
export const adminRequirementsAPI = {
  get: () => api.get('/admin-requirements'),
  getRbac: () => api.get('/admin-requirements/rbac'),
  getAny: async () => {
    try {
      return await api.get('/admin-requirements/rbac');
    } catch (error) {
      console.warn('adminRequirementsAPI.getAny: /admin-requirements/rbac failed, falling back to /admin-requirements', error?.message || error);
      return await api.get('/admin-requirements');
    }
  },
  save: (data) => api.post('/admin-requirements', data),
};

// Telephony config exposed to clients/admins (non-sensitive fields)
export const telephonyConfigAPI = {
  get: () => api.get('/telephony-config')
};

// Server-side PBX call wrapper (uses server-held API key)
export const pbxServerAPI = {
  initiate: (adminId, extension, phone) => api.post('/pbx/call', { adminId, extension, phone })
};

// WhatsApp Edge Functions API
// Calls Supabase Edge Functions directly for WhatsApp notifications
const WHATSAPP_FUNCTIONS_BASE_URL = 'https://giskyueandklposlokkq.supabase.co/functions/v1/';

// PBX Call API
export const pbxCallAPI = {
  initiate: (pbxApiUrl, pbxApiKey, extension, phone) =>
    fetch(`${pbxApiUrl}/calls/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': pbxApiKey,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ extension, destination: phone })
    }).then(r => r.json()),

  getCdr: (pbxApiUrl, pbxApiKey, params = {}) => {
    const query = new URLSearchParams(params).toString();
    return fetch(`${pbxApiUrl}/calls/cdr?${query}`, {
      headers: {
        'X-API-KEY': pbxApiKey,
        'Accept': 'application/json'
      }
    }).then(r => r.json());
  },

  // Fetch CDR from PBX and save each record into call_logs table
  syncCdrToCallLogs: async (pbxApiUrl, pbxApiKey, params = {}) => {
    const query = new URLSearchParams(params).toString();
    const cdrData = await fetch(`${pbxApiUrl}/calls/cdr?${query}`, {
      headers: {
        'X-API-KEY': pbxApiKey,
        'Accept': 'application/json'
      }
    }).then(r => r.json());

    // cdrData expected shape: { records: [...] } or array directly
    const records = Array.isArray(cdrData) ? cdrData : (cdrData.records || cdrData.data || []);

    if (!records.length) return { synced: 0, records: [] };

    // Map PBX CDR fields → call_logs schema (same as Plivo)
    const mapped = records.map(r => ({
      provider: 'pbx',
      call_uuid: r.call_id || r.uuid || r.id,
      from_number: r.caller || r.from || r.src,
      to_number: r.destination || r.to || r.dst,
      direction: r.direction || (r.call_type === 'outbound' ? 'outbound' : 'inbound'),
      duration: r.duration || r.billsec || 0,
      bill_duration: r.billsec || r.duration || 0,
      call_status: r.disposition || r.status || r.hangup_cause || 'unknown',
      initiated_time: r.start_time || r.calldate || r.created_at,
      answer_time: r.answer_time || r.answered_at || null,
      end_time: r.end_time || r.hangup_time || null,
      recording_url: r.recording_url || r.record_file || null,
    }));

    // Bulk save to our backend call_logs table
    const saveResponse = await callLogsAPI.savePbxCdr(mapped);
    return { synced: mapped.length, records: mapped, response: saveResponse.data };
  }
};

// WhatsApp Manual Dispatch API
// Routes to our own Express backend — dynamic database-driven template dispatch
export const whatsappEdgeAPI = {
  /**
   * Trigger a WhatsApp notification via a Supabase Edge Function.
   * @param {string} functionName - 'order-confirmation' | 'subscription-confirmation' | 'vendor-po-webhook'
   * @param {string} orderId - The ID of the order or PO
   * @param {string} tableName - The table name for the webhook handler
   */
  triggerNotification: (functionName, orderId, tableName) => {
    const token = getAccessToken();
    return axios.post(
      `${WHATSAPP_FUNCTIONS_BASE_URL}${functionName}`,
      { order_id: orderId, table_name: tableName },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        timeout: 15000,
      }
    );
  }
};

export const outboundAPI = {
  initiate: (data) => api.post('/outbound/initiate', data)
};


export default api;
